/**
 * Card displaying a single version's details with statistics.
 */

import { useState } from 'react';
import { Card, Typography, Space, Descriptions, Tag, Button, Popconfirm, message } from 'antd';
import { ClockCircleOutlined, UserOutlined, FileTextOutlined, DownOutlined, UpOutlined, DeleteOutlined } from '@ant-design/icons';
import ChangeRecordList from './ChangeRecordList';
import { deleteVersion } from '@/api/versions';
import type { RequirementVersion } from '@/types';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

interface VersionCardProps {
  version: RequirementVersion;
  projectId: number;
  isLatest?: boolean;
  onDeleted?: () => void;
}

const VersionCard: React.FC<VersionCardProps> = ({ version, projectId, isLatest, onDeleted }) => {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const {
    id,
    version_number,
    version_info,
    publisher,
    published_at,
    statistics,
  } = version;

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteVersion(projectId, id);
      message.success(`版本 ${version_number} 已删除`);
      onDeleted?.();
    } catch {
      message.error('删除失败，请重试');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <Tag color="blue">{version_number}</Tag>
          <ClockCircleOutlined />
          <Text type="secondary">{formatDate(published_at)}</Text>
        </Space>
      }
      extra={
        isLatest && (
          <Popconfirm
            title={`确认删除版本 ${version_number}？`}
            description="删除后，该版本的变更记录将被清除，下次发布时会重新生成。"
            onConfirm={handleDelete}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deleting}
              size="small"
            >
              删除版本
            </Button>
          </Popconfirm>
        )
      }
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item
          label={
            <Space>
              <UserOutlined />
              <span>发布人</span>
            </Space>
          }
        >
          {publisher.display_name || publisher.username}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space>
              <FileTextOutlined />
              <span>版本信息</span>
            </Space>
          }
        >
          <Paragraph
            style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
            ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}
          >
            {version_info}
          </Paragraph>
        </Descriptions.Item>

        <Descriptions.Item label="变更统计">
          <Space size="large">
            <Text>
              总变更数: <Text strong>{statistics.total_changes}</Text>
            </Text>
            {statistics.created > 0 && (
              <Text>
                新增: <Text type="success" strong>{statistics.created}</Text>
              </Text>
            )}
            {statistics.updated > 0 && (
              <Text>
                修改: <Text type="warning" strong>{statistics.updated}</Text>
              </Text>
            )}
            {statistics.deleted > 0 && (
              <Text>
                删除: <Text type="danger" strong>{statistics.deleted}</Text>
              </Text>
            )}
          </Space>
        </Descriptions.Item>
      </Descriptions>

      {/* 变更记录独立于 Descriptions 外，以使用完整卡片宽度 */}
      {statistics.total_changes > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Button
            type="link"
            icon={expanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setExpanded(!expanded)}
            style={{ padding: 0 }}
          >
            {expanded ? '收起详细变更记录' : '查看详细变更记录（含变更前后值）'}
          </Button>
          {expanded && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <ChangeRecordList projectId={projectId} versionId={id} />
            </div>
          )}
        </div>
      ) : (
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>暂无变更记录</Text>
      )}
    </Card>
  );
};

export default VersionCard;
